import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Descriptions,
  Empty,
  Form,
  Input,
  Message,
  Select,
  Space,
  Spin,
  Typography,
} from '@arco-design/web-react';
import { listCdrs, getCdr, type Cdr } from '../api/cdr';
import {
  SUMMARY_CATEGORY_OPTIONS,
  SUMMARY_RESULT_OPTIONS,
  upsertCallSummary,
} from '../api/callSummaries';
import { getAISummary, generateAISummary, type AISummaryDraft } from '../api/assist';
import { useAgentStore } from '../store/agent';
import { usePhoneStore } from '../store/phone';
import { fmtDuration, maskPhone } from '../utils/format';
import { talkSeconds } from '../utils/callTime';
import { getIntentName, useIntentNames } from '../utils/intentName';
import DetailSection from './DetailSection';
import FormModal, {FORM_MODAL_WIDTH} from './FormModal';
import JsonArrayInput from './JsonArrayInput';
import PillTag from './PillTag';
import StatusTag from './StatusTag';
import TicketCreateModal from './TicketCreateModal';

export interface SummaryModalProps {
  visible: boolean;
  /**
   * 已定位的 CDR（呼叫记录页补录场景传入）；
   * 不传则定位「本通电话的 CDR」，三级精度递减：
   * ① cdrEvent：后端 CDR 落库后经管理台 WS 推送的 cdr_id（最准，可能晚于弹窗几百 ms）；
   * ② 基线轮询：以响铃时快照的 cdrBaseline 为准，等 id 更大的记录出现（1s 间隔 ×3）；
   * ③ 基线缺失（快照失败）退化为时间窗校验。
   */
  cdr?: Cdr | null;
  /** WS cdr_written 事件（id=cdr_id，at=前端接收时刻，本机时钟） */
  cdrEvent?: { id: number; at: number };
  /** WS ai_ticket_created 事件（v1.8.1：AI 自动建单完成，弹窗展示横幅 + 查看跳转） */
  autoTicketEvent?: {
    cdr_id: number;
    /** 工单号口径全站统一为工单主键 id（主列表「工单号」列即此值） */
    ticket_id: number;
    type: string;
    priority: string;
    source: string;
    intent_code: string;
    at: number;
  };
  /** submitted=true 表示本次提交成功 */
  onClose: (submitted: boolean) => void;
}

type Phase = 'loading' | 'ready' | 'notfound';

const MAX_ATTEMPTS = 4; // 首次 + 1s 间隔重试 ×3（CDR 落库滞后 ~300ms，第 2 次即可命中）

/** 判定查到的 CDR 是否刚挂断这通（而非上一通旧记录） */
function isCurrentCallCdr(c: Cdr, openedAt: number): boolean {
  const baseline = usePhoneStore.getState().cdrBaseline;
  if (baseline != null) return c.id > baseline;
  // 基线缺失兜底：只接受结束时间在弹窗打开前 60s 内的记录
  const end = Date.parse(c.end_time || '');
  return !Number.isNaN(end) && end >= openedAt - 60_000;
}

/** 挂断后服务小结弹窗：分类/结果/备注/是否回访 → POST /call-summaries（upsert） */
export default function SummaryModal({ visible, cdr, cdrEvent, autoTicketEvent, onClose }: SummaryModalProps) {
  useIntentNames();
  const [form] = Form.useForm();
  const agentId = useAgentStore((s) => s.agentId);
  const [phase, setPhase] = useState<Phase>('loading');
  const [record, setRecord] = useState<Cdr | null>(null);
  const [saving, setSaving] = useState(false);
  /** AI 小结草稿（v1.5 P1-B） */
  const [aiDraft, setAiDraft] = useState<AISummaryDraft | null>(null);
  const [generating, setGenerating] = useState(false);
  const [aiFromAi, setAiFromAi] = useState(false); // 表单值来源是否为 AI 草稿（展示 Tag）
  /** 弹窗打开时刻（本机时钟，供 cdrEvent/时间窗兜底比对） */
  const openedAtRef = useRef(0);
  /** 已定位标记：WS 推送与轮询两条路径先到为准，后者不再覆盖 */
  const settledRef = useRef(false);
  const recordRef = useRef<Cdr | null>(null);
  recordRef.current = record;

  useEffect(() => {
    if (!visible) return;
    form.resetFields();
    setRecord(null);
    settledRef.current = false;
    openedAtRef.current = Date.now();
    if (cdr?.id) {
      setRecord(cdr);
      setPhase('ready');
      return;
    }
    if (!agentId) {
      setPhase('notfound');
      return;
    }
    setPhase('loading');
    const openedAt = openedAtRef.current;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tryLoad = async (attempt: number) => {
      try {
        const { list } = await listCdrs({ agent_id: agentId, page: 1, pageSize: 1 });
        if (cancelled || settledRef.current) return;
        // 必须等到本通电话的 CDR：id 大于响铃基线（旧记录直接拒绝，继续等落库）
        if (list[0] && isCurrentCallCdr(list[0], openedAt)) {
          settledRef.current = true;
          setRecord(list[0]);
          setPhase('ready');
          return;
        }
      } catch {
        /* 失败按未找到重试 */
      }
      if (cancelled || settledRef.current) return;
      if (attempt < MAX_ATTEMPTS) {
        timer = setTimeout(() => tryLoad(attempt + 1), 1000);
      } else {
        setPhase('notfound');
      }
    };
    tryLoad(1);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [visible, cdr, agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // WS cdr_written 优先通道：事件常晚于弹窗几百 ms（ESL→落库→redis→WS），
  // 到达后经新鲜度校验直接按 id 拉详情，抢在轮询前定位
  useEffect(() => {
    if (!visible || cdr?.id || !cdrEvent || settledRef.current) return;
    const baseline = usePhoneStore.getState().cdrBaseline;
    const fresh =
      baseline != null
        ? cdrEvent.id > baseline // 有基线：id 必须更新（拒绝上一通残留事件）
        : cdrEvent.at >= openedAtRef.current - 2000; // 无基线：本机时钟比对（拒绝旧事件）
    if (!fresh) return;
    let cancelled = false;
    getCdr(cdrEvent.id)
      .then((full) => {
        if (cancelled || settledRef.current || !full?.id) return;
        settledRef.current = true;
        setRecord(full);
        setPhase('ready');
      })
      .catch(() => undefined); // 拉取失败交给轮询兜底
    return () => {
      cancelled = true;
    };
  }, [visible, cdrEvent, cdr]); // eslint-disable-line react-hooks/exhaustive-deps

  // 加载 AI 小结草稿并预填表单（v1.5 P1-B）
  const applyAiDraft = (d: AISummaryDraft) => {
    if (!d.found) {
      setAiDraft(null);
      return;
    }
    setAiDraft(d);
    // AI 草稿值预填（用户可改；仅当表单仍为空/默认值时覆盖，避免覆盖用户已输入）
    const cur = form.getFieldsValue();
    if (!cur.category || cur.category === '咨询') form.setFieldValue('category', d.category || '咨询');
    if (!cur.result || cur.result === '已解决') form.setFieldValue('result', d.result || '已解决');
    if (!cur.note) form.setFieldValue('note', d.note || '');
    form.setFieldValue('follow_up', !!d.follow_up);
    if (cur.tags === undefined) form.setFieldValue('tags', d.tags || '');
    setAiFromAi(true);
  };

  // record 就绪后拉取 AI 草稿；若 AI 小结晚于弹窗到达，短轮询兜底（≤12s）
  useEffect(() => {
    if (!visible || !record?.id) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let tries = 0;
    const poll = async () => {
      if (cancelled || tries >= 4) return;
      tries += 1;
      try {
        const d = await getAISummary('call', record.id!);
        if (cancelled) return;
        if (d.found) {
          applyAiDraft(d);
          return;
        }
      } catch { /* 忽略 */ }
      timer = setTimeout(poll, 3000);
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [visible, record?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // 重生成 AI 小结
  const onGenerate = async () => {
    if (!record?.id) return;
    setGenerating(true);
    try {
      await generateAISummary('call', record.id);
      Message.success('AI 小结生成中，稍候自动填入');
      // 等 worker 完成（轮询，最多 4 次 × 3s）
      let done = false;
      for (let i = 0; i < 4 && !done; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const d = await getAISummary('call', record.id!);
        if (d.found) {
          applyAiDraft(d);
          done = true;
        }
      }
      if (!done) Message.warning('AI 小结暂未就绪，可稍后点击重新生成');
    } catch { /* 拦截器已提示 */ } finally {
      setGenerating(false);
    }
  };

  // 定位后补拉单对象详情：融合 record_url（录音回放）/完整字段（列表查询不含）
  const [recordFull, setRecordFull] = useState<Cdr | null>(null);
  const [playRec, setPlayRec] = useState(false);
  useEffect(() => {
    if (!visible || !record?.id) return;
    let cancelled = false;
    if (record.record_url) {
      setRecordFull(record);
      return;
    }
    getCdr(record.id)
      .then((full) => {
        if (cancelled || !full?.id) return;
        setRecordFull(full);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [visible, record]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!visible) setPlayRec(false);
  }, [visible]);

  // 发起工单（嵌套建单弹窗，关联当前通话；需回访默认跟随小结勾选）
  const [ticketOpen, setTicketOpen] = useState(false);

  // AI 自动建单横幅（v1.8.1）：本通 CDR 匹配才显示（WS 事件可能晚于弹窗到达）
  const [autoTicket, setAutoTicket] = useState<typeof autoTicketEvent>();
  useEffect(() => {
    if (!autoTicketEvent) return;
    if (record?.id && Number(autoTicketEvent.cdr_id) === record.id) {
      setAutoTicket(autoTicketEvent);
    }
  }, [autoTicketEvent, record?.id]);

  const onSubmit = async () => {
    const v = await form.validate();
    if (!record?.id) return;
    setSaving(true);
    try {
      await upsertCallSummary({
        cdr_id: record.id,
        category: v.category,
        result: v.result,
        note: v.note || '',
        follow_up: !!v.follow_up,
        tags: v.tags || '',
      });
      Message.success('服务小结已保存');
      onClose(true);
    } catch { /* 拦截器已提示 */ } finally {
      setSaving(false);
    }
  };

  const remoteNumber = record
    ? record.direction === 'inbound'
      ? record.caller
      : record.callee
    : '';

  /** 服务小结为挂断必填项：记录已定位时不允许「关闭即跳过」（记录没定位到时仍要能关掉弹窗） */
  const handleCancel = () => {
    if (phase === 'ready') {
      Message.warning('服务小结为必填项，请填写后提交');
      return;
    }
    onClose(false);
  };

  return (
    <FormModal
      form={form}
      title="服务小结"
      visible={visible}
      onCancel={handleCancel}
      maskClosable={false}
      width={FORM_MODAL_WIDTH.form}
      footer={
        phase === 'ready' ? (
          <Space>
            <Button onClick={() => setTicketOpen(true)}>创建工单</Button>
            <Button loading={generating} onClick={onGenerate}>
              AI 生成小结
            </Button>
            {/* 服务小结为挂断必填项：不再给「跳过」出口，备注与分类/结果一样必填 */}
            <Button type="primary" loading={saving} onClick={onSubmit}>
              提交小结
            </Button>
          </Space>
        ) : phase === 'notfound' ? (
          <Button type="primary" onClick={() => onClose(false)}>
            关闭
          </Button>
        ) : null
      }
    >
      {phase === 'loading' && (
        <div style={{ textAlign: 'center', padding: '32px 0' }}>
          <Spin />
          <Typography.Paragraph type="secondary" style={{ marginTop: 12 }}>
            正在获取通话记录…
          </Typography.Paragraph>
        </div>
      )}
      {phase === 'notfound' && (
        <Empty
          description={
            <Space direction="vertical" size={4}>
              <Typography.Text>未找到通话记录</Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                可稍后在「呼叫记录」中补录服务小结
              </Typography.Text>
            </Space>
          }
        />
      )}
      {phase === 'ready' && record && (
        <>
          {/* 本通通话只读信息块挂分节标题；AI 建议条/自动建单横幅各自已成块，小结表单不再另起标题 */}
          <DetailSection title="通话信息">
            <Descriptions
              column={3}
              size="small"
              style={{ marginBottom: 12 }}
              data={[
                {
                  label: '方向',
                  // 与 CDR 列表/通话详情 StatusTag 同口径：inbound 蓝 / outbound 青
                  value: <StatusTag value={record.direction} />,
                },
                { label: '对方号码', value: maskPhone(remoteNumber) || '-' },
                // 通话时长=接通→挂断（与录音/报表同口径）；未接通显示 '-'，不与 0 秒混淆
                { label: '通话时长', value: talkSeconds(record) > 0 ? fmtDuration(talkSeconds(record)) : '-' },
                {
                  label: '录音',
                  value: recordFull?.record_url ? (
                    <Space size={6}>
                      <Button size="mini" type="text" onClick={() => setPlayRec((v) => !v)}>
                        {playRec ? '收起录音' : '播放录音'}
                      </Button>
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: 12, cursor: 'pointer' }}
                        onClick={() => window.open(`/calls?id=${record.id}`, '_blank')}
                      >
                        查看转写 →
                      </Typography.Text>
                    </Space>
                  ) : (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      无录音
                    </Typography.Text>
                  ),
                },
              ]}
            />
          </DetailSection>
          {playRec && recordFull?.record_url && (
            <audio controls autoPlay style={{ width: '100%', marginBottom: 12 }} src={recordFull.record_url} />
          )}
          {aiDraft?.found && (
            <div
              style={{
                border: '1px solid rgba(var(--arcoblue-6), 0.35)',
                background: 'rgba(var(--arcoblue-6), 0.06)',
                borderRadius: 8,
                padding: '8px 10px',
                marginBottom: 12,
              }}
            >
              <Space size={6} style={{ marginBottom: 6 }}>
                <PillTag color="arcoblue" size="sm">
                  AI 建议
                </PillTag>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  已填入表单，可修改后提交
                </Typography.Text>
              </Space>
              <Typography.Paragraph style={{ marginBottom: 4, whiteSpace: 'pre-wrap' }}>
                {aiDraft.note || aiDraft.summary || '（无内容）'}
              </Typography.Paragraph>
              <Space size={6} wrap>
                {aiDraft.category && <PillTag size="sm">{aiDraft.category}</PillTag>}
                {aiDraft.result && <PillTag size="sm">{aiDraft.result}</PillTag>}
                {aiDraft.follow_up && (
                  <PillTag size="sm" color="orange">
                    需回访
                  </PillTag>
                )}
              </Space>
            </div>
          )}
          {autoTicket && (
            <Alert
              type="success"
              style={{ marginBottom: 12 }}
              title="AI 已自动创建工单"
              content={
                <Space direction="vertical" style={{ width: '100%' }}>
                  <span>
                    {autoTicket.ticket_id ? `工单号 ${autoTicket.ticket_id}` : '工单'}（{autoTicket.type} / {autoTicket.priority}
                    {autoTicket.intent_code ? ` / 意图 ${getIntentName(autoTicket.intent_code)}` : ''}）已关联本通通话并启动审核流程
                  </span>
                  <Button size="small" type="text" onClick={() => window.open('/tickets', '_blank')}>
                    查看工单 →
                  </Button>
                </Space>
              }
            />
          )}
          <Form
            form={form}
            layout="vertical"
            initialValues={{ category: '咨询', result: '已解决' }}
          >
            <div className="wb-form-grid2">
              <Form.Item field="category" label="分类" rules={[{ required: true, message: '请选择分类' }]}>
                <Select placeholder="请选择" options={SUMMARY_CATEGORY_OPTIONS} />
              </Form.Item>
              <Form.Item field="result" label="处理结果" rules={[{ required: true, message: '请选择处理结果' }]}>
                <Select placeholder="请选择" options={SUMMARY_RESULT_OPTIONS} />
              </Form.Item>
            </div>
            <Form.Item field="note" label="备注" rules={[{ required: true, message: '请填写服务小结' }]}>
              <Input.TextArea maxLength={500} rows={3} placeholder="通话要点、客户诉求等" />
            </Form.Item>
            <Form.Item field="follow_up" label=" " triggerPropName="checked">
              <Checkbox>需要回访</Checkbox>
            </Form.Item>
            <Form.Item
              field="tags"
              label={
                <span>
                  业务标签
                  <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                    （AI 已生成，可回车增删调整）
                  </Typography.Text>
                </span>
              }
            >
              <JsonArrayInput placeholder="输入标签后回车添加" />
            </Form.Item>
          </Form>
        </>
      )}
      {/* 嵌套发起工单：关联当前通话，需回访默认跟随小结勾选 */}
      <TicketCreateModal
        visible={ticketOpen}
        cdr={record}
        defaultFollowUp={!!form.getFieldValue?.('follow_up')}
        onClose={() => setTicketOpen(false)}
      />
    </FormModal>
  );
}
